import { FC, FormEvent, useEffect, useMemo, useState } from 'react';

export interface VariantAttributeDefinition {
  id: string;
  name: string;
  erpId: number;
  defaultValue: string | null;
  options: string[];
}

export interface VariantDraftValue {
  id: number;
  selectedValue: string;
  attributeName?: string;
}

export interface VariantDraft {
  id: string;
  name: string;
  code: string;
  description: string;
  upc: string;
  retailPrice: string;
  staffPrice: string;
  isTemporarilyUnavailable: boolean;
  isAvailableInPos: boolean;
  isAvailableInEcommerce: boolean;
  isSoldAsSingleItem: boolean;
  erpId?: number | null;
  isActive?: boolean;
  productVariantValues?: VariantDraftValue[];
}

interface AddVariantModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (variant: VariantDraft) => void;
  initialVariant?: VariantDraft | null;
  templateAttributes?: VariantAttributeDefinition[] | null;
  masterName: string;
}

const generateId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const createEmptyVariant = (): VariantDraft => ({
  id: generateId(),
  name: '',
  code: '',
  description: '',
  upc: '',
  retailPrice: '',
  staffPrice: '',
  isTemporarilyUnavailable: false,
  isAvailableInPos: false,
  isAvailableInEcommerce: false,
  isSoldAsSingleItem: false,
  erpId: null,
  isActive: true,
  productVariantValues: [],
});

const AddVariantModal: FC<AddVariantModalProps> = ({
  open,
  onClose,
  onSave,
  initialVariant,
  templateAttributes,
  masterName,
}) => {
  const [form, setForm] = useState<VariantDraft>(initialVariant ?? createEmptyVariant());
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(initialVariant?.id ?? null);

  const handleInputChange = (event: FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const target = event.currentTarget;
    const { name, value } = target;

    if ('type' in target && target.type === 'checkbox') {
      const checkbox = target as HTMLInputElement;
      setForm((prev) => ({
        ...prev,
        [name]: checkbox.checked,
      }));
      setError(null);
      return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError(null);
  };

  const resetState = () => {
    setForm(initialVariant ?? createEmptyVariant());
    setError(null);
    setSubmitted(false);
    setEditingId(initialVariant?.id ?? null);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const isValidUpc = (value: string) => {
    if (!value.trim()) {
      return true;
    }
    return /^[a-z0-9]+$/i.test(value.trim());
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitted(true);

    if (!form.name.trim()) {
      setError('Variant name is required.');
      return;
    }

    if (!isValidUpc(form.upc)) {
      setError('UPC must contain only letters and numbers.');
      return;
    }

    onSave({ ...form, id: editingId ?? generateId() });
    resetState();
    onClose();
  };

  useEffect(() => {
    if (open) {
      setForm(initialVariant ?? createEmptyVariant());
      setEditingId(initialVariant?.id ?? null);
      setError(null);
      setSubmitted(false);
    }
  }, [initialVariant, open]);

  const mergedVariantValues: VariantDraftValue[] = useMemo(() => {
    const attributes = templateAttributes ?? [];
    if (!attributes.length) {
      return [];
    }

    const existingById = new Map<number, VariantDraftValue>();
    (form.productVariantValues ?? []).forEach((value) => {
      if (typeof value.id === 'number') {
        existingById.set(value.id, value);
      }
    });

    return attributes.map((attribute) => {
      const attrId = Number(attribute.erpId);
      const existing = existingById.get(attrId);
      if (existing) {
        return existing;
      }

      const defaultOption =
        attribute.defaultValue && attribute.options.includes(attribute.defaultValue)
          ? attribute.defaultValue
          : '';

      return {
        id: attrId,
        selectedValue: defaultOption,
        attributeName: attribute.name,
      };
    });
  }, [templateAttributes, form.productVariantValues]);

  const generatedName = useMemo(() => {
    const base = (masterName ?? '').trim();
    const suffixParts =
      mergedVariantValues
        .map((value) => value.selectedValue?.trim())
        .filter((value): value is string => Boolean(value && value.length > 0));

    const parts: string[] = [];
    if (base) {
      parts.push(base);
    }
    if (suffixParts.length) {
      parts.push(...suffixParts);
    }

    return parts.join(' ');
  }, [masterName, mergedVariantValues]);

  useEffect(() => {
    if (!open) return;
    setForm((prev) => ({
      ...prev,
      name: generatedName,
    }));
  }, [generatedName, open]);

  const handleVariantAttributeChange = (attribute: VariantAttributeDefinition, value: string) => {
    setForm((prev) => {
      const currentValues = prev.productVariantValues ?? [];
      const attrId = Number(attribute.erpId);
      const nextValues: VariantDraftValue[] = [];
      let found = false;

      currentValues.forEach((item) => {
        if (item.id === attrId) {
          found = true;
          nextValues.push({
            id: attrId,
            selectedValue: value,
            attributeName: attribute.name,
          });
        } else {
          nextValues.push(item);
        }
      });

      if (!found) {
        nextValues.push({
          id: attrId,
          selectedValue: value,
          attributeName: attribute.name,
        });
      }

      return {
        ...prev,
        productVariantValues: nextValues,
      };
    });
  };

  const containerClassNames = useMemo(
    () =>
      'fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4 backdrop-blur-sm',
    [],
  );

  const panelClassNames = useMemo(
    () =>
      'flex w-full max-h-[92vh] max-w-4xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white text-slate-900 shadow-2xl',
    [],
  );

  if (!open) {
    return null;
  }

  return (
    <div
      className={containerClassNames}
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-variant-modal-title"
    >
      <div className={panelClassNames}>
        <div className="flex w-full items-center border-b border-slate-200 bg-slate-50 px-6 py-4">
          <h2 id="add-variant-modal-title" className="flex-1 text-lg font-semibold text-slate-900">
            {editingId ? 'Edit Variant' : 'Add Variant'}
          </h2>
          <button
            type="button"
            className="ml-auto inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-300 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/40"
            onClick={handleClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>



        <div className="flex-1 overflow-y-auto px-6 py-5">
          <form onSubmit={handleSubmit} className="grid gap-4 md:grid-cols-2">
            <label className="flex flex-col gap-1 text-xs font-semibold text-slate-700 md:col-span-2">
              Variant Name *
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleInputChange}
                disabled
                className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                required
              />
              <span className="text-[11px] font-normal text-slate-400">
                Variant name is generated automatically using the master product name and the
                values selected under <strong>Variant Attributes</strong>. It cannot be edited
                directly here.
              </span>
            </label>

            <label className="flex flex-col gap-1 text-xs font-semibold text-slate-700">
              Variant Code
              <input
                type="text"
                name="code"
                value={form.code}
                onChange={handleInputChange}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </label>

            <label className="flex flex-col gap-1 text-xs font-semibold text-slate-700">
              UPC
              <input
                type="text"
                name="upc"
                value={form.upc}
                onChange={handleInputChange}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </label>

            <label className="flex flex-col gap-1 text-xs font-semibold text-slate-700">
              Retail Price
              <input
                type="text"
                name="retailPrice"
                value={form.retailPrice}
                onChange={handleInputChange}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </label>

            <label className="flex flex-col gap-1 text-xs font-semibold text-slate-700">
              Staff Price
              <input
                type="text"
                name="staffPrice"
                value={form.staffPrice}
                onChange={handleInputChange}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </label>

            <label className="flex flex-col gap-1 text-xs font-semibold text-slate-700 md:col-span-2">
              Description
              <textarea
                name="description"
                value={form.description}
                onChange={handleInputChange}
                rows={3}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </label>

            <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 md:col-span-2">
              <input
                type="checkbox"
                name="isActive"
                checked={form.isActive ?? true}
                onChange={handleInputChange}
                className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary/40"
              />
              <span>
                Variant is active
                <span className="block text-[11px] font-normal text-slate-400">
                  Inactive variants are hidden from service selection in orders and manager approval.
                </span>
              </span>
            </label>

            {templateAttributes && templateAttributes.length > 0 ? (
              <>
                <div className="md:col-span-2 mt-4 border-t border-slate-200 pt-3">
                  <p className="text-xs font-semibold text-slate-500">
                    Variant Attributes
                  </p>
                  <p className="mt-1 text-[11px] text-slate-400">
                    Select values for each attribute defined in the chosen template.
                  </p>
                </div>

                {templateAttributes.map((attribute) => {
                  const currentValue = mergedVariantValues.find(
                    (item) => item.id === Number(attribute.erpId),
                  )?.selectedValue ?? '';

                  return (
                    <label
                      key={attribute.id}
                      className="flex flex-col gap-1 text-xs font-semibold text-slate-700 md:col-span-1"
                    >
                      {attribute.name}
                      <select
                        value={currentValue}
                        onChange={(event) =>
                          handleVariantAttributeChange(attribute, event.currentTarget.value)
                        }
                        className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                      >
                        <option value="">Select value</option>
                        {attribute.options.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </label>
                  );
                })}
              </>
            ) : null}

            {submitted && error && (
              <p className="md:col-span-2 text-sm text-danger">{error}</p>
            )}

            <div className="md:col-span-2 mt-2 flex items-center justify-end gap-3 border-t border-slate-200 pt-4">
              <button
                type="button"
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-primary/20"
                onClick={handleClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white shadow transition hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                {editingId ? 'Save changes' : 'Add Variant'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddVariantModal;
